<template>
  <div>
      <div class="title"> A Failed Attempt to Solve Captchas </div>
      <div class="date"> March 22nd, 2021 </div>

      <div class="content">

        I was doing a tedious task that involved a lot of repetition, so I tried to automate it. 
        However, every so often I was asked to answer the following pattern of Captchas.

        <br/><br/>
        <img class="pixel-img" style="display: block; margin: auto; width: 40%" src="static/captcha/problem.png">
        <br/>

        The answer to the captcha is 401202. I gathered some data and found that these captchas strictly use 2 different fonts, and 
        always have 4-6 numbers. It seemed pretty easy to randomly generate these captchas, so I thought it was a good idea to use deep learning
        to solve the captcha, since my dataset would be almost infinitely large. 

        <br/><br/>

        To familiarize myself, I first trained a 7-layer CNN on the MNIST dataset that achieved 100% test accuracy. Using a similar
        CNN architecture, I randomly generated captchas on the spot and cropped out each digit, feeding it to the CNN. I also fed the CNN
        some inputs that were not digits. I used Cross Entropy as my loss function. To my surprise, the CNN achieved 95% accuracy (after 
        changing the architecture and tuning hyper-parameters). Below are some example inputs, as well as the architecture I used.

        <div style="display: flex; justify-content: center;">
            <img class="pixel-img" style="width: 10%; margin: 0px 15px" src="static/captcha/3.png">
            <img class="pixel-img" style="width: 10%; margin: 0px 15px" src="static/captcha/4.png">
            <img class="pixel-img" style="width: 10%; margin: 0px 15px" src="static/captcha/9.png">
        </div>

        <pre class="code">
    CaptchaNet(
        (cnn): Sequential(
            (0): Conv2d(1, 128, kernel_size=(5, 5), stride=(2, 2))
            (1): PReLU(num_parameters=1)
            (2): Conv2d(128, 128, kernel_size=(3, 3), stride=(1, 1))
            (3): PReLU(num_parameters=1)
            (4): Conv2d(128, 256, kernel_size=(3, 3), stride=(1, 1))
            (5): PReLU(num_parameters=1)
            (6): Conv2d(256, 512, kernel_size=(3, 3), stride=(1, 1))
            (7): PReLU(num_parameters=1)
            (8): Conv2d(512, 512, kernel_size=(3, 3), stride=(1, 1))
            (9): PReLU(num_parameters=1)
            (10): Conv2d(512, 256, kernel_size=(1, 1), stride=(1, 1))
            (11): PReLU(num_parameters=1)
            (12): Conv2d(256, 11, kernel_size=(1, 1), stride=(1, 1))
            (13): PReLU(num_parameters=1)
            (14): Softmax(dim=1)
        )
    )</pre>

        Although the model performed really well on classifying cropped out digits, I had no idea how to extract the location of
        each digit for a real captcha. The first thing I tried was a sliding window approach. To do this, I simply fed the entire captcha 
        into the CNN. However, this approach gave me a seemingly garbled output that I did not know how to process further (shown below).
        <span class="italic"> This is where I got stuck. </span>

        <br/><br/>

        <div style="display: flex; justify-content: center;">
            <div style="text-align: center;"> 
                <img class="pixel-img" style="height: 100px" src="static/captcha/in.png">
                <p> Input image </p>
            </div>
            <div style="text-align: center;"> 
                <img class="pixel-img" style="height: 100px; margin-left: 20px" src="static/captcha/out.png">
                <p> Sliding window output </p>
            </div>
        </div>

        I knew I had half of the problem solved; my network correctly classified digits, but did not locate them. This led me to read research
        papers on object detection. I tried replicating the ideas in the paper <a class="link" href="https://arxiv.org/abs/1506.02640"> "You Only
        Look Once: Unified, Real-Time Object Detection" by Joseph Redmon, Santosh Divvala, Ross Girshick, and Ali Farhadi. </a> It seemed promising,
        but this is what my model output looked like. 

        <br/><br/>
        <img class="pixel-img" style="display: block; margin: auto; height: 250px;" src="static/captcha/yolo_out.png">
        <br/>
        
        It seemed like the model wasn't learning where the numbers actually were, but how to efficiently place bounding boxes in a way that were
        sometimes right. I tried changing the model's architecture, increasing its capacity, and changing hyper-parameters, but nothing seemed to
        change the model's behavior. I tried putting less numbers in my input images, but then the model tended to predict that no numbers were contained
        in the image. This is where I am now. I'm not sure why my model isn't working. Maybe I should train it on the same dataset used in the YOLO 
        research paper to confirm I implemented it correctly.

        <br/><br/>

        <div class="quote"> Key takeaways </div>
        <ul>
            <li> Data preparation matters <span class="italic">a lot</span>. Clean data can be much better than more data. </li>
            <li> Designing deep network architectures is difficult. </li>
            <li> Micro-changes to the model's architecture should be done on an exponential scale for asymptotic analysis. </li>
            <li> It can be hard to figure out why a model isn't performing well. Is it a problem with data? architecture? optimization? hyper-parameters? </li>
        </ul>

        Honestly, I think neural networks are great only for approximating certain types of functions.
        I think the function approximation problem needs to be re-evaluated at its most simple scale.
        I had an idea to use Fourier Transforms for function approximation, but I don't know how to cultivate it further.

        <br/><br/>

        <span class="quote"> Although this project failed, I learned a lot, but I also have so many unanswered questions. </span>

        <br/><br/>

        I'll revisit this sometime in the future.

        <br/><br/>
        <br/><br/>
        <br/><br/>

      </div>
  </div>
</template>

<script>

export default {
  name: 'CaptchaAttempt',
}

</script>


<style scoped>

.code {
    display: block;
    background: rgb(240, 240, 240);
    text-align: left;
    font-size: 11pt;
    line-height: 13pt;
    padding-top: 5px;
    padding-bottom: 5px;
}

.pixel-img {
    image-rendering: optimizeSpeed;             /* STOP SMOOTHING, GIVE ME SPEED  */
    image-rendering: -moz-crisp-edges;          /* Firefox                        */
    image-rendering: -o-crisp-edges;            /* Opera                          */
    image-rendering: -webkit-optimize-contrast; /* Chrome (and eventually Safari) */
    image-rendering: pixelated; /* Chrome */
    image-rendering: optimize-contrast;         /* CSS3 Proposed                  */
    -ms-interpolation-mode: nearest-neighbor; 
}

.italic {
    font-style: italic;
}


</style>
